import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClusterLogoService, ImageSize } from 'src/api/v2';
import type { FileUploadResult } from '@innovationnorway/cl-react';

export const LOGO_ALLOWED_FILETYPES: Array<string> = ['image/png', 'image/jpeg'];
import { CLUSTER_LOGO_KEY } from 'src/constants/hook-keys';

export const useClusterLogo = (clusterId: string) => {
  const [clusterLogo] = useState(new ClusterLogoService());
  const { t: tError } = useTranslation('error');
  const [logoString, setLogoString] = useState('');
  const [reader] = useState(new FileReader());
  const queryClient = useQueryClient();

  const logoQuery = useQuery({
    queryKey: [CLUSTER_LOGO_KEY, clusterId],
    queryFn: () => clusterLogo.getClusterLogo(clusterId, ImageSize.Medium),
    enabled: !!clusterId,
    retry: false,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (!clusterId || !logoQuery.data || logoQuery.isError || logoQuery.isFetching) {
      setLogoString('');
      return;
    }

    reader.readAsDataURL(logoQuery.data.data);
    reader.onloadend = () => {
      setLogoString(reader.result as string);
    };
  }, [clusterId, logoQuery.data, logoQuery.dataUpdatedAt, logoQuery.isError, logoQuery.isFetching, reader]);

  return {
    logoQuery,

    uploadLogo: async (files: File[], _fileCount: number): Promise<FileUploadResult | undefined> => {
      if (!files.length) return undefined;
      const file = files[0];
      const result = await uploadLogoFile(
        file,
        clusterId,
        clusterLogo,
        queryClient,
        tError,
        CLUSTER_LOGO_KEY,
      );
      if (result?.status) {
        queryClient.invalidateQueries([CLUSTER_LOGO_KEY]);
      }
      return result;
    },
    deleteLogo: async (_id?: string): Promise<void> => {
      await deleteLogo(clusterLogo, queryClient, CLUSTER_LOGO_KEY);
      queryClient.removeQueries([CLUSTER_LOGO_KEY, clusterId]);
    },
    logoString,
  };
};

async function uploadLogoFile(
  file: File,
  clusterId: string,
  service: ClusterLogoService,
  queryClient: QueryClient,
  tError: TFunction,
  invalidationQueryKey: string | undefined,
): Promise<FileUploadResult | undefined> {
  try {
    if (file === null || file === undefined) {
      return { status: false, message: tError('noFileSelected') };
    }
    if (!clusterId) {
      return { status: false, message: tError('noClusterMemberId') };
    }
    if (!LOGO_ALLOWED_FILETYPES.includes(file.type)) {
      return { message: tError('wrongFileFormat'), status: false };
    }
    // Max file size 1 MB.
    if (file.size > 1048576) {
      return { message: tError('tooBigFile'), status: false };
    }

    await service.createClusterLogo({
      fileName: file.name,
      data: file,
    });

    if (invalidationQueryKey) queryClient.invalidateQueries([invalidationQueryKey]);
    return { status: true, message: `${clusterId}-${Date.now()}` };
  } catch (_e: any) {
    if (_e.Status === 422) {
      return { status: false, message: tError('malwareDetected') };
    }
    return { message: tError('somethingWentWrongTryAgain'), status: false };
  }
}

async function deleteLogo(
  service: ClusterLogoService,
  queryClient: QueryClient,
  invalidationQueryKey: string | undefined,
): Promise<void> {
  await service.deleteClusterLogo();
  if (invalidationQueryKey) queryClient.invalidateQueries([invalidationQueryKey]);
}
